import { gql } from "graphql-tag";
import {
  ProductCardRegularFragment,
  ProductSummaryFragment,
} from "entity/product/api/fragments";
import { CategoryFragment } from "entity/category/api/fragments";
import { AdvertisedCountersFragment } from "entity/counters/api/fragments";
import {
  UserProfileFragment,
  RegionFragment,
  HomeBlogCategoryFragment,
  MainCategoryFragment,
  CartItemFragment,
  FiltersConfigFragment,
  MetaInformationFragment,
  GeoObjectFragment,
  PharmacyFragment,
  ClusterFragment,
  ProductInstructionsFragment,
  PromoFragment,
  LoyaltyFragment,
  BrandFragment,
  SettingsFragment,
  DeliveryFragment,
  PickupStoreFragment,
  PickupStorePointFragment,
  PreOrderFragment,
  OrderFragment,
  GroupFragment,
} from "./fragments";

export const validateMindboxCard = gql`
  query validateMindboxCard($number: String!) {
    validateMindboxCard(number: $number)
  }
`;

export const RegionsQuery = gql`
  query regions {
    regions {
      ...RegionFragment
    }
  }
  ${RegionFragment}
`;

export const SuggestRegionQuery = gql`
  query suggestRegion($query: String!) {
    suggestRegion(query: $query) {
      ...GeoObjectFragment
    }
  }
  ${GeoObjectFragment}
`;

export const isRegisteredQuery = gql`
  query isRegistered($phone: String!) {
    isRegistered(phone: $phone)
  }
`;

export const getProfileQuery = gql`
  query getProfile {
    getProfile {
      ...UserProfileFragment
    }
  }
  ${UserProfileFragment}
`;

export const DetectRegionQuery = gql`
  query detectRegion($ip: String!) {
    detectRegion(ip: $ip) {
      ...RegionFragment
    }
  }
  ${RegionFragment}
`;

export const CategoriesQuery = gql`
  query categories($regionID: ID!) {
    categories(regionID: $regionID) {
      ...MainCategoryFragment
      # cant spread fragment in recursion
      children {
        ...CategoryFragment
        children {
          ...CategoryFragment
          children {
            ...CategoryFragment
            children {
              ...CategoryFragment
            }
          }
        }
      }
    }
  }
  ${CategoryFragment}
  ${MainCategoryFragment}
`;

export const BannersQuery = gql`
  query Banners($regionID: ID!, $type: AdvertisementType!, $query: String) {
    advertisement(regionID: $regionID, type: $type, query: $query) {
      banner
      advertiser
      counters {
        ...AdvertisedCountersFragment
      }
    }
  }
  ${AdvertisedCountersFragment}
`;

export const SettingsQuery = gql`
  query settings($regionID: ID!) {
    settings(regionID: $regionID) {
      ...SettingsFragment
    }
  }
  ${SettingsFragment}
`;

export const HomeListingsQuery = gql`
  query HomeListingsQuery(
    $regionID: ID!
    $url: String!
    $articleSearch: ArticleSearch!
    $categoriesSearch: BlogCategorySearch!
    $bannerType: AdvertisementType!
  ) {
    advertisement(regionID: $regionID, type: $bannerType) {
      banner
      advertiser
      counters {
        ...AdvertisedCountersFragment
      }
    }

    seasonSelections(regionID: $regionID) {
      advertiser
      alias
      image {
        height
        url
        width
      }
      name
      url
    }

    partners: brandSlider {
      id
      image
      isActive
      name
      url
    }

    articles(search: $articleSearch) {
      items {
        alias
        id
        image {
          l {
            url
          }
        }
        name
        category {
          ...HomeBlogCategoryFragment
        }
      }
    }

    blogCategories(search: $categoriesSearch) {
      items {
        ...HomeBlogCategoryFragment
      }
    }

    drugsAlphabet(regionID: $regionID, type: drugs) {
      name
      url
    }

    brandsAlphabet: drugsAlphabet(regionID: $regionID, type: brands) {
      name
      url
    }

    diseasesAlphabet: drugsAlphabet(regionID: $regionID, type: diseases) {
      name
      url
    }

    metaInformation(url: $url, regionID: $regionID) {
      ...MetaInformationFragment
    }
  }
  ${HomeBlogCategoryFragment}
  ${AdvertisedCountersFragment}
  ${MetaInformationFragment}
`;

export const GoodsOfDay = gql`
  query GoodsOfDay($regionID: ID!) {
    goodsOfDay(regionID: $regionID) {
      items {
        ...ProductCardRegularFragment
      }
    }
  }
  ${ProductCardRegularFragment}
`;

export const ProductsSearchQuery = gql`
  query productsSearch($search: ProductSearch!) {
    products(search: $search) {
      items {
        ...ProductSummaryFragment
        reviewsCount
      }
      total
      description
    }
  }
  ${ProductSummaryFragment}
`;

export const CatalogPageQuery = gql`
  query CatalogPage($search: ProductSearch!, $regionID: ID!, $url: String!) {
    products(search: $search) {
      items {
        ...ProductSummaryFragment
        reviewsCount
      }
      total
      description
    }
    metaInformation(regionID: $regionID, url: $url) {
      ...MetaInformationFragment
    }
  }
  ${ProductSummaryFragment}
  ${MetaInformationFragment}
`;

export const WidgetsQuery = gql`
  query widgets($input: WidgetInput!) {
    widgets(input: $input) {
      items {
        items {
          ...ProductSummaryFragment
        }
        alias
      }
    }
  }

  ${ProductSummaryFragment}
`;

export const FiltersConfigQuery = gql`
  query filterConfig(
    $regionID: ID!
    $categoryCode: ID
    $categoryAlias: String
    $type: FiltersConfigType! = other
    $query: String
    $tagCode: String
    $brandCode: String
    $withoutSubcategories: Boolean = false
    $groupCode: String
  ) {
    filtersConfig(
      regionID: $regionID
      categoryID: $categoryCode # deprecated. remove in INT-1710
      categoryAlias: $categoryAlias
      type: $type
      query: $query
      tagCode: $tagCode
      brandCode: $brandCode
      groupCode: $groupCode
    ) {
      ...FiltersConfigFragment
    }
  }

  ${FiltersConfigFragment}
`;

export const BrowsingHistoryQuery = gql`
  query browsingHistory($regionID: ID!, $ids: [ID!]) {
    products(
      search: {
        regionID: $regionID
        productIDs: $ids
        paginator: { limit: 10, offset: 0 }
      }
    ) {
      items {
        ...ProductCardRegularFragment
      }
    }
  }
  ${ProductCardRegularFragment}
`;

export const MetaInformationQuery = gql`
  query metaInformation($regionID: ID!, $url: String!) {
    metaInformation(regionID: $regionID, url: $url) {
      ...MetaInformationFragment
    }
  }
  ${MetaInformationFragment}
`;

export const ProductInstructionsQuery = gql`
  query productInstructions($slug: ID!, $regionID: ID!) {
    product(slug: $slug, regionID: $regionID) {
      instructions {
        ...ProductInstructionsFragment
      }
      certs
    }
  }
  ${ProductInstructionsFragment}
`;

export const SuggestAddressQuery = gql`
  query suggestAddress($query: String!, $regionID: ID) {
    suggestAddress(query: $query, regionID: $regionID) {
      formattedAddress
      street
      houseNumber
      suburb
      postcode
      state
      stateCode
      stateDistrict
      country
      city
    }
  }
`;

export const OrderByPhonePageQuery = gql`
  query orderByPhonePage(
    $url: String!
    $regionID: ID!
    $pharmacistPaginator: NewPaginator!
  ) {
    settings(regionID: $regionID) {
      ...SettingsFragment
    }

    teamPersons(type: pharmacist, paginator: $pharmacistPaginator) {
      total
      items {
        ... on Pharmacist {
          id
          code
          photo
          fullName
          description
          specialization
        }
      }
    }

    teamInfo {
      pharmacists {
        description
      }
    }
    metaInformation(url: $url, regionID: $regionID) {
      ...MetaInformationFragment
    }
  }
  ${SettingsFragment}
  ${MetaInformationFragment}
`;

export const CartQuery = gql`
  query cart($regionID: ID!) {
    cart(regionID: $regionID) {
      groups {
        items {
          ...CartItemFragment
        }
      }
    }
  }
  ${CartItemFragment}
`;

export const PreOrderQuery = gql`
  query preOrder($regionID: ID!, $step: StepOrderType) {
    preOrder(regionID: $regionID, step: $step) {
      ...PreOrderFragment
    }
  }
  ${PreOrderFragment}
`;

export const OrderQuery = gql`
  query order($id: ID!) {
    order(id: $id) {
      ...OrderFragment
    }
  }
  ${OrderFragment}
`;

export const userLastOrderQuery = gql`
  query userLastOrderQuery {
    userLastOrder {
      ...OrderFragment
    }
  }
  ${OrderFragment}
`;

export const AutocompleteQuery = gql`
  query AutocompleteQuery($regionID: ID!, $query: String!, $size: Int) {
    autocomplete(regionID: $regionID, query: $query, size: $size) {
      sts {
        st
        amount
      }
      taps {
        tap
        relatedSearch
      }
      products {
        ...ProductSummaryFragment
      }
    }
  }
  ${ProductSummaryFragment}
`;

export const SearchHelpQuery = gql`
  query SearchHelpQuery {
    searchHelp {
      url
      value
    }
  }
`;

export const RegionQuery = gql`
  query region($regionID: ID!) {
    region(regionID: $regionID) {
      ...RegionFragment
    }
  }

  ${RegionFragment}
`;

export const PharmaciesOnMapQuery = gql`
  query PharmaciesOnMap(
    $xMin: Float!
    $xMax: Float!
    $yMin: Float!
    $yMax: Float!
    $zoom: Int!
    $search: [String!]
    $pharmacyIds: [ID!]
  ) {
    pharmacyMap(
      xMin: $xMin
      xMax: $xMax
      yMin: $yMin
      yMax: $yMax
      zoom: $zoom
      search: $search
      pharmacyIds: $pharmacyIds
    ) {
      __typename
      ... on Pharmacy {
        ...PharmacyFragment
      }
      ... on Cluster {
        ...ClusterFragment
      }
    }
  }
  ${PharmacyFragment}
  ${ClusterFragment}
`;

export const PharmaciesListQuery = gql`
  query PharmaciesList(
    $search: [String!]
    $paginator: NewPaginator
    $regionID: ID
    $searchByName: Boolean
    $pharmacyIds: [ID!]
  ) {
    searchPharmacy(
      search: $search
      paginator: $paginator
      regionID: $regionID
      searchByName: $searchByName
      pharmacyIds: $pharmacyIds
    ) {
      items {
        ...PharmacyFragment
      }
      total
    }
  }
  ${PharmacyFragment}
`;

export const PharmacyCardQuery = gql`
  query PharmacyCard($id: ID!) {
    pharmacy(id: $id) {
      ...PharmacyFragment
    }
  }
  ${PharmacyFragment}
`;

export const updateFavoritePharmacy = gql`
  mutation updateFavoritePharmacy($regionID: ID!, $id: ID!) {
    updateFavoritePharmacy(regionID: $regionID, id: $id) {
      ...PharmacyFragment
    }
  }
  ${PharmacyFragment}
`;

export const PharmacyPageQuery = gql`
  query PharmacyPage($id: ID!, $regionID: ID!, $url: String!) {
    pharmacy(id: $id) {
      ...PharmacyFragment
    }
    metaInformation(regionID: $regionID, url: $url) {
      ...MetaInformationFragment
    }
  }
  ${PharmacyFragment}
  ${MetaInformationFragment}
`;

export const PrescriptionsQuery = gql`
  query PrescriptionQuery($regionID: ID!) {
    prescriptions(regionID: $regionID) {
      url
      prescriptions {
        id
        number
        dosage
        products
      }
    }
  }
`;

export const GroupQuery = gql`
  query Group($regionID: ID!, $code: ID!, $paginator: NewPaginator!) {
    group(regionID: $regionID, code: $code) {
      ...GroupFragment
    }
  }
  ${GroupFragment}
`;

export const PromoPageQuery = gql`
  query PromoPage(
    $regionID: ID!
    $url: String!
    $promoAlias: ID!
    $search: ProductSearch!
  ) {
    products(search: $search) {
      items {
        ...ProductSummaryFragment
      }
      total
    }
    promo(promoAlias: $promoAlias, regionID: $regionID) {
      ...PromoFragment
    }
    metaInformation(url: $url, regionID: $regionID) {
      ...MetaInformationFragment
    }
  }
  ${PromoFragment}
  ${MetaInformationFragment}
  ${ProductSummaryFragment}
`;

export const BrandQuery = gql`
  query BrandPage($alias: String!, $regionID: ID!, $url: String!) {
    brand(alias: $alias) {
      ...BrandFragment
    }
    metaInformation(regionID: $regionID, url: $url) {
      ...MetaInformationFragment
    }
  }
  ${BrandFragment}
  ${MetaInformationFragment}
`;

export const TeamPersonsQuery = gql`
  query teamPersons($type: PersonType!, $paginator: NewPaginator!) {
    teamPersons(type: $type, paginator: $paginator) {
      total
      items {
        ... on Doctor {
          id
          code
          photo
          fullName
          specialization
        }
        ... on Author {
          id
          code
          photo
          fullName
          specialization
        }
        ... on Staff {
          id
          code
          photo
          fullName
          position
        }
        ... on Pharmacist {
          id
          code
          photo
          fullName
          description
          specialization
        }
      }
    }
  }
`;

export const RelatedProductsQuery = gql`
  query RelatedProducts($categoryID: ID!, $regionID: ID!) {
    relatedProducts(categoryID: $categoryID, regionID: $regionID) {
      ...ProductSummaryFragment
    }
  }
  ${ProductSummaryFragment}
`;

export const LoyaltiesQuery = gql`
  query loyalties {
    loyalties {
      ...LoyaltyFragment
    }
  }
  ${LoyaltyFragment}
`;

export const DeliveryZonesQuery = gql`
  query DeliveryZones($regionID: ID!) {
    deliveryZones(regionID: $regionID) {
      zones {
        type
        color
        coordinates
      }
    }
  }
`;

export const AddressCoordinatesQuery = gql`
  query AddressCoordinates($regionID: ID!, $addressText: String!) {
    addressCoordinates(regionID: $regionID, addressText: $addressText) {
      address {
        id
        name
        address
        lat
        long
      }
    }
  }
`;

export const PharmacySearchMapQuery = gql`
  query pharmacySearchMap(
    $xMin: Float!
    $xMax: Float!
    $yMin: Float!
    $yMax: Float!
    $zoom: Int!
    $search: PharmacySearch!
    $receiveType: String!
    $regionID: ID!
    $withoutSubcategories: Boolean = false
  ) {
    pharmacySearchMap(
      xMin: $xMin
      xMax: $xMax
      yMin: $yMin
      yMax: $yMax
      zoom: $zoom
      search: $search
      receiveType: $receiveType
      regionID: $regionID
    ) {
      items {
        __typename
        ... on Cluster {
          ...ClusterFragment
        }
        ... on PickupStorePoint {
          ...PickupStorePointFragment
        }
      }
      total
      filters {
        ...FiltersConfigFragment
      }
    }
  }
  ${ClusterFragment}
  ${PickupStorePointFragment}
  ${FiltersConfigFragment}
`;

export const PickupStoreQuery = gql`
  query pickupStore($pharmacyId: ID!, $regionID: ID!) {
    pickupStore(pharmacyId: $pharmacyId, regionID: $regionID) {
      pharmacy {
        ...PharmacyFragment
      }
      deliveries {
        ...DeliveryFragment
      }
    }
  }
  ${PharmacyFragment}
  ${DeliveryFragment}
`;

export const PharmacySearchListQuery = gql`
  query pharmacySearchList(
    $paginator: NewPaginator
    $regionID: ID!
    $search: PharmacySearch!
    $withoutSubcategories: Boolean = false
  ) {
    pharmacySearchList(
      paginator: $paginator
      regionID: $regionID
      search: $search
    ) {
      items {
        ...PickupStoreFragment
      }
      total
      filters {
        ...FiltersConfigFragment
      }
    }
  }
  ${PickupStoreFragment}
  ${FiltersConfigFragment}
`;

export const PharmacySearchHintsQuery = gql`
  query pharmacySearchHints(
    $type: String!
    $regionID: ID!
    $search: PharmacySearch!
  ) {
    pharmacySearchHints(type: $type, regionID: $regionID, search: $search) {
      __typename
      ...PharmacyFragment
    }
  }
  ${PharmacyFragment}
`;
export const PrescriptionQuery = gql`
  query addPrescription($prescriptionOwner: PrescriptionOwner!) {
    prescription(prescriptionOwner: $prescriptionOwner) {
      hash
    }
  }
`;
